import { useMemo } from 'react'
import { Grabber } from '../components/Grabber'
import { useNestedSheetHeight } from '../hooks/useNestedSheetHeight'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { TokenAndChainPickerScreen } from '../screens/TokenAndChainPickerScreen'
import { Box, useTheme } from '../theme/restyle'
import type { AvailableChain } from '../transfer/availableChains'
import {
  type DepositAsset,
  type DepositSourceToken,
  assetId,
  resolveDepositSelection,
} from '../transfer/depositAssets'

export function DepositAssetPicker({
  assets,
  chains,
  selectedToken,
  loading,
  onSelect,
  onClose,
}: {
  assets: DepositAsset[]
  chains: AvailableChain[]
  selectedToken?: DepositSourceToken
  loading: boolean
  onSelect: (token: DepositSourceToken, chain: AvailableChain) => void
  onClose: () => void
}) {
  const { t } = useFunkitTranslation()
  const height = useNestedSheetHeight()
  const theme = useTheme()
  const selectedAsset = selectedToken
    ? assets.find((asset) => asset.id === assetId(selectedToken.symbol))
    : undefined
  const tokens = useMemo(
    () =>
      assets.map((asset) => ({
        symbol: asset.symbol,
        chainIds: asset.tokens.map((token) => token.chainId),
      })),
    [assets],
  )
  return (
    <Box
      borderTopLeftRadius="modal"
      borderTopRightRadius="modal"
      style={{ height, backgroundColor: theme.background.modal }}
    >
      <TokenAndChainPickerScreen
        title={t('selectAsset.combinedTitle')}
        tokens={tokens}
        chains={chains}
        initialSelection={
          selectedToken && selectedAsset
            ? { symbol: selectedAsset.symbol, chainId: selectedToken.chainId }
            : undefined
        }
        loading={loading}
        onConfirm={(selection) => {
          const resolved = resolveDepositSelection(assets, chains, selection)
          if (resolved) {
            onSelect(resolved.token, resolved.chain)
          }
        }}
        onBack={onClose}
      />
      <Grabber />
    </Box>
  )
}
